import React, { ReactNode, forwardRef } from 'react';
import {
  Checkbox,
  CheckboxProps,
  FormControl,
  FormControlLabel,
  FormControlProps,
  FormHelperText,
  FormGroup,
  FormLabel,
  Grid,
  Box,
} from '@mui/material';
import { Control, Controller, FieldPath, FieldValues } from 'react-hook-form';
import { RedAsterisk } from '../RedAsterisk';

type NestedFieldPosition = 'right' | 'bottom';

type OptionType = {
  value: any;
  label: ReactNode;

  // The option could trigger the display of a nested field.
  nestedField?: ReactNode | ((index: number) => ReactNode);
  nestedFieldPosition?: NestedFieldPosition;
};

type MultiSelectFieldProps<
  T extends FieldValues = any,
  TFieldName extends FieldPath<FieldValues> = FieldPath<FieldValues>
> = FormControlProps & {
  control: Control<T>;
  name: TFieldName;
  label: ReactNode;
  multiSelectProps?: CheckboxProps;
  options: OptionType[];
  exclusiveOptions?: any[]; // exclusiveOptions are the values that, when selected, clear other selections.
  touched?: boolean;
  required?: boolean;
  displayInColumns?: number;
  renderError?: boolean;
};

const MultiSelectField = forwardRef<HTMLButtonElement, MultiSelectFieldProps>(
  function MultiSelectField(props, ref) {
    const {
      control,
      name,
      label,
      multiSelectProps,
      touched: defaultTouched,
      options,
      exclusiveOptions,
      required,
      displayInColumns = 2,
      renderError = true,
      ...rest
    } = props;

    return (
      <Controller
        control={control}
        name={name}
        render={({ field, fieldState, formState }) => {
          const { error, isTouched: fieldTouched } = fieldState;
          const touched = defaultTouched || fieldTouched;

          // Ensure the field value is always an array
          const currentValue: any[] = Array.isArray(field.value)
            ? field.value
            : [];

          const handleChange =
            (optionValue: any) => (e: React.ChangeEvent<HTMLInputElement>) => {
              const isChecked = e.target.checked;
              let newValue = [...currentValue];

              // If this option is part of the exclusive options set the array to only contain this option
              if (exclusiveOptions && exclusiveOptions.includes(optionValue)) {
                newValue = isChecked ? [optionValue] : [];
              } else {
                // Remove any exclusive options if present.
                if (exclusiveOptions) {
                  newValue = newValue.filter(
                    (val) => !exclusiveOptions.includes(val)
                  );
                }
                if (isChecked) {
                  // Add the option if it’s not already selected
                  if (!newValue.includes(optionValue)) {
                    newValue.push(optionValue);
                  }
                } else {
                  // Remove the option if unchecked
                  newValue = newValue.filter((val) => val !== optionValue);
                }
              }

              field.onChange(newValue);
              field.onBlur();
            };

          function _renderHelperText() {
            if (touched && error && renderError) {
              return (
                <FormHelperText error sx={{ mx: 0 }}>
                  {error.message}
                </FormHelperText>
              );
            }
          }

          return (
            <FormControl {...rest}>
              <FormLabel
                sx={{
                  mb: '16px',
                }}
              >
                {label}
                {required && <RedAsterisk />}
              </FormLabel>
              <FormGroup>
                <Grid container spacing={2}>
                  {options.map((option, index) => {
                    const isSelected = currentValue.includes(option.value);
                    return (
                      <Grid
                        item
                        xs={12 / displayInColumns}
                        key={index}
                        sx={{
                          '&.MuiGrid-root': {
                            paddingTop: '6px',
                          },
                          display: 'flex',
                          // We set the nested field on the right or below (default) the checkbox
                          ...(option.nestedFieldPosition === 'right' && {
                            flexDirection: 'row',
                            alignItems: 'center',
                          }),
                          ...((!option.nestedFieldPosition ||
                            option.nestedFieldPosition === 'bottom') && {
                            flexDirection: 'column',
                          }),
                        }}
                      >
                        <FormControlLabel
                          key={index}
                          control={
                            <Checkbox
                              {...multiSelectProps}
                              ref={ref}
                              checked={currentValue.includes(option.value)}
                              onChange={handleChange(option.value)}
                            />
                          }
                          label={option.label}
                        />

                        {/* Render nested field if there's any and if the option is selected */}
                        {option.nestedField && isSelected && (
                          <Box
                            sx={{
                              ...((!option.nestedFieldPosition ||
                                option.nestedFieldPosition === 'bottom') && {
                                ml: 4,
                                mt: 1,
                                mb: 2,
                              }),
                            }}
                          >
                            {typeof option.nestedField === 'function'
                              ? option.nestedField(index)
                              : option.nestedField}
                          </Box>
                        )}
                      </Grid>
                    );
                  })}
                </Grid>
              </FormGroup>
              {_renderHelperText()}
            </FormControl>
          );
        }}
      />
    );
  }
);

export default MultiSelectField;
